import {
    PsHttpService,
    type PatSnapCommonResponseData,
    PsHttpError,
    isPatSnapErrorResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import type { RequestConfig } from '@patsnap/common-ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { merge } from 'lodash';
import type { SkillApiService } from '../skill-api';
import type {
    WebSkillListParams,
    WebSkillListResponse,
    WebSkillDetailItem,
    WebSkillCreateRequest,
    WebSkillCreateData,
    WebSkillEnableData,
    WebSkillDeleteData,
    WebSkillUpdateRequest,
    WebSkillUpdateData,
    WebSkillImportRequest,
    WebSkillImportData,
    WebSkillSecurityCheckRequest,
    SkillSecurityCheckData,
    SharedSkillInfo,
    CreateSkillFromSkillIdData,
    WebSkillImportFromShareLinkRequest,
    WebSkillImportFromShareLinkData,
    SkillSharedResourceMetadata,
    SkillShareGenerateForm,
    SkillShareCreateData,
} from '../../types';

@injectable()
export class SkillApiServiceImpl extends PsHttpService implements SkillApiService {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/hub-api/web/skill`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    // 覆盖 request 方法以处理后端返回 data.data 为 null 的情况
    override request<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config.headers = merge({}, this.options.headers, config.headers);
        config.baseURL = config.baseURL || this.options.baseURL;
        return this.httpClient<T>(config)
            .then((res) => {
                const data = res.data;
                if (isPatSnapErrorResponseData(data)) {
                    // 处理 data.data 为 null 的情况，使用 error_msg 作为备选
                    const errorMessage = data.data?.error_message ?? data.error_msg ?? 'Unknown error';
                    const error = new PsHttpError(errorMessage, {
                        cause: data,
                    });
                    throw error;
                }
                return res.data;
            });
    }

    getList(params?: WebSkillListParams) {
        return this.get<PatSnapCommonResponseData<WebSkillListResponse>>({
            // url: '/list',
            url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/web/skill/list`,
            params,
        }).then(res => res.data as WebSkillListResponse);
    }

    getDetail(id: string) {
        return this.get<PatSnapCommonResponseData<WebSkillDetailItem>>({
            url: `/by-id/${encodeURIComponent(id)}`,
        }).then(res => res.data as WebSkillDetailItem);
    }

    create(data: WebSkillCreateRequest) {
        return this.post<PatSnapCommonResponseData<WebSkillCreateData>>({
            url: '/create',
            data,
        }).then(res => res.data as WebSkillCreateData);
    }

    update(id: string, data: WebSkillUpdateRequest) {
        return this.request<PatSnapCommonResponseData<WebSkillUpdateData>>({
            url: `/by-id/${encodeURIComponent(id)}`,
            method: 'PUT',
            data,
        }).then(res => res.data as WebSkillUpdateData);
    }

    enable(id: string) {
        return this.post<PatSnapCommonResponseData<WebSkillEnableData>>({
            url: `/by-id/${encodeURIComponent(id)}/enable`,
        }).then(res => res.data as WebSkillEnableData);
    }

    disable(id: string) {
        return this.post<PatSnapCommonResponseData<WebSkillEnableData>>({
            url: `/by-id/${encodeURIComponent(id)}/disable`,
        }).then(res => res.data as WebSkillEnableData);
    }

    remove(id: string) {
        return this.request<PatSnapCommonResponseData<WebSkillDeleteData>>({
            url: `/by-id/${encodeURIComponent(id)}`,
            method: 'DELETE',
        }).then(res => res.data as WebSkillDeleteData);
    }

    exportSkill(id: string) {
        return this.request<Blob>({
            url: `/by-id/${encodeURIComponent(id)}/export`,
            method: 'GET',
            responseType: 'blob',
        });
    }

    importSkill(data: WebSkillImportRequest) {
        const formData = new FormData();
        formData.append('file', data.file);
        if (data.name) {
            formData.append('name', data.name);
        }
        if (data.overwrite !== undefined) {
            formData.append('overwrite', String(data.overwrite));
        }
        return this.post<PatSnapCommonResponseData<WebSkillImportData>>({
            url: '/import',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(res => res.data as WebSkillImportData);
    }

    validateImportSecurity(data: WebSkillSecurityCheckRequest) {
        const formData = new FormData();
        formData.append('file', data.file);
        return this.post<PatSnapCommonResponseData<SkillSecurityCheckData>>({
            url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/web/skill/security/validate-import`,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(res => res.data as SkillSecurityCheckData);
    }

    // ==================== 分享相关 ====================
    generateShareLink(skillId: string, data?: SkillShareGenerateForm) {
        return this.post<PatSnapCommonResponseData<SkillShareCreateData>>({
            url: `/by-id/${encodeURIComponent(skillId)}/share/generate`,
            data: data ?? {},
        }).then(res => res.data as SkillShareCreateData);
    }

    importFromShareLink(data: WebSkillImportFromShareLinkRequest) {
        return this.post<PatSnapCommonResponseData<WebSkillImportFromShareLinkData>>({
            url: '/import/from-share-link',
            data: data,
        }).then(res => res.data as WebSkillImportFromShareLinkData);
    }

    getSharedResourceMetadata(shareCode: string) {
        return this.get<PatSnapCommonResponseData<SkillSharedResourceMetadata>>({
            url: `/shared-resources/${encodeURIComponent(shareCode)}`,
        }).then(res => res.data as SkillSharedResourceMetadata);
    }

    // 分享落地页：把分享资源元数据适配为落地页所需信息
    async getSharedSkillInfo(shareCode: string): Promise<SharedSkillInfo> {
        const meta = await this.getSharedResourceMetadata(shareCode);
        return {
            id: meta.share_code,
            name: meta.title || meta.original_filename,
            description: meta.description,
            shared_by: meta.sharer_name,
            updated_at: meta.updated_at,
            password_protected: meta.passcode_required,
        };
    }

    // 分享落地页：通过分享码安装，返回新建技能的 id 与 name
    async createSkillFromSkillId(shareCode: string, passcode?: string): Promise<CreateSkillFromSkillIdData> {
        const res = await this.importFromShareLink({ share_link: shareCode, passcode });
        return { id: res.id, name: res.name };
    }
}
